import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Stack,
  Avatar,
  Divider,
  Tooltip,
  Slide,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import FlagIcon from '@mui/icons-material/Flag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TagIcon from '@mui/icons-material/Tag';
import { useDispatch, useSelector } from 'react-redux';
import { deleteTask, getCreatedTasks } from '../../redux/features/taskManagementSlice';
import { useSnackbar } from 'notistack';
import GlobalAllTypeFileView from '../../components/Fileupload/GlobalAllTypeFileView';
import { Category } from '@mui/icons-material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HistoryIcon from '@mui/icons-material/History';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TaskComments from './components/TaskComments';
import TaskActivity from './components/TaskActivity';
import TaskRating from './components/TaskRating';

// ------------------ CONFIG ------------------
const priorityConfig = {
  1: { label: 'Critical', color: '#B91C1C', bg: 'rgba(185, 28, 28, 0.1)' },
  2: { label: 'High', color: '#F43F5E', bg: 'rgba(244, 63, 94, 0.1)' },
  3: { label: 'Medium', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)' },
  4: { label: 'Low', color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.1)' },
};

const statusConfig = {
  0: {
    label: 'Pending',
    color: '#F59E0B',
    bg: '#FFF7ED',
    border: '#FDBA74',
    icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} />,
  },
  1: {
    label: 'In Progress',
    color: '#3B82F6',
    bg: '#EFF6FF',
    border: '#93C5FD',
    icon: <AutorenewIcon sx={{ fontSize: 14 }} />,
  },
  2: {
    label: 'Completed',
    color: '#10B981',
    bg: '#F0FDF4',
    border: '#86EFAC',
    icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
  },
  5: {
    label: 'Completed',
    color: '#10B981',
    bg: '#F0FDF4',
    border: '#86EFAC',
    icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
  },
};

const getAvatarColor = (name) => {
  const colors = ['#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#3B82F6', '#F43F5E'];
  const hash = name?.split('').reduce((a, c) => a + c.charCodeAt(0), 0) || 0;
  return colors[hash % colors.length];
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ------------------ INFO ROW ------------------
const InfoRow = ({ icon, label, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1.5,
        background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 16, color: '#6366F1' } })}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          color: '#94A3B8',
          fontSize: '0.7rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          display: 'block',
          mb: 0.3,
        }}
      >
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  </Box>
);

// ------------------ MAIN DIALOG ------------------
const TaskDetailDialog = ({ open, onClose, task, onDelete , filters}) => {
  // const [confirmDelete, setConfirmDelete] = useState(false);

  const { deleteLoading } = useSelector((state) => state.taskManagement);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTab] = useState(0);

  if (!task) return null;

  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('cred'))?.profile || {};
    } catch {
      return {};
    }
  })();
  const canRate =
    (currentUser?.name && currentUser.name === task.created_by_name) ||
    (currentUser?.name && currentUser.name === task.assigned_by_name);

  const priority = priorityConfig[task.priority] || priorityConfig[1];
  const status = statusConfig[task.assignment_status] || statusConfig[task.current_status] || statusConfig[0];
  const isOverdue =
    task.expected_delivery_date &&
    new Date(task.expected_delivery_date) < new Date() &&
    task.assignment_status !== 5 &&
    task.current_status !== 2;

  // Parse attachments (comma-separated string or array)
  const attachmentList = Array.isArray(task.attachments)
    ? task.attachments
    : typeof task.attachments === 'string' && task.attachments.trim()
      ? task.attachments
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean)
      : [];

  const handleDeleteClick = (task_id) => {
    dispatch(deleteTask(task_id)).then((res) => {
      if (res.payload.status === 'success') {
        enqueueSnackbar(res.payload.message, { variant: 'success' });
        dispatch(getCreatedTasks(filters))
        onClose?.();
      } else {
        enqueueSnackbar(res.payload.message, { variant: 'error' });
        onClose?.();
      }
    });
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          background: '#FFFFFF',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Gradient Header */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)',
          px: 4,
          py: 3,
          color: '#fff',
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: 'absolute',
            top: -40,
            right: -40,
            width: 160,
            height: 160,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            right: 80,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
          }}
        />

        {/* Close button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: '#fff',
            background: 'rgba(255,255,255,0.12)',
            backdropFilter: 'blur(10px)',
            width: 36,
            height: 36,
            zIndex: 1,
            '&:hover': {
              background: 'rgba(255,255,255,0.22)',
              transform: 'rotate(90deg)',
            },
            transition: 'all 0.3s',
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <AssignmentIndIcon sx={{ fontSize: 26, color: '#fff' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, pr: 5 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '1.15rem',
                lineHeight: 1.3,
                letterSpacing: '-0.3px',
              }}
            >
              {task.title}
            </Typography>
          </Box>
        </Stack>

        {/* Status chips row */}
        <Stack direction="row" spacing={1} sx={{ mt: 2, position: 'relative', zIndex: 1 }}>
          <Chip
            icon={React.cloneElement(status.icon, {
              sx: { color: '#fff !important', fontSize: 14 },
            })}
            label={status.label}
            size="small"
            sx={{
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.72rem',
              border: '1px solid rgba(255,255,255,0.25)',
              height: 26,
              '& .MuiChip-icon': { color: '#fff !important', ml: '6px' },
            }}
          />
          <Chip
            icon={<FlagIcon sx={{ color: '#fff !important', fontSize: 14 }} />}
            label={`${priority.label} Priority`}
            size="small"
            sx={{
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.72rem',
              border: '1px solid rgba(255,255,255,0.25)',
              height: 26,
              '& .MuiChip-icon': { color: '#fff !important', ml: '6px' },
            }}
          />
          {isOverdue && (
            <Chip
              icon={<WarningAmberIcon sx={{ color: '#fff !important', fontSize: 14 }} />}
              label="Overdue"
              size="small"
              sx={{
                background: 'rgba(239, 68, 68, 0.9)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.72rem',
                height: 26,
                '& .MuiChip-icon': { color: '#fff !important', ml: '6px' },
              }}
            />
          )}
        </Stack>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 0, background: '#FFFFFF' }}>
        {/* Tab bar */}
        <Box sx={{ borderBottom: '1px solid #EEF2F6', px: 2, background: '#fff' }}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                minHeight: 44,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.82rem',
                color: '#64748B',
              },
              '& .Mui-selected': { color: '#6366F1 !important' },
              '& .MuiTabs-indicator': { background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', height: 3, borderRadius: 3 },
            }}
          >
            <Tab iconPosition="start" icon={<InfoOutlinedIcon sx={{ fontSize: 17 }} />} label="Details" />
            <Tab iconPosition="start" icon={<ChatBubbleOutlineIcon sx={{ fontSize: 17 }} />} label="Comments" />
            <Tab iconPosition="start" icon={<HistoryIcon sx={{ fontSize: 17 }} />} label="Activity" />
            {canRate && <Tab iconPosition="start" icon={<StarBorderRoundedIcon sx={{ fontSize: 18 }} />} label="Rating" />}
          </Tabs>
        </Box>

        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <TaskComments taskId={task.task_id} />
          </Box>
        )}
        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <TaskActivity taskId={task.task_id} />
          </Box>
        )}
        {tab === 3 && canRate && (
          <Box sx={{ p: 3 }}>
            <TaskRating
              taskId={task.task_id}
              createdByName={task.created_by_name}
              assignedByName={task.assigned_by_name}
            />
          </Box>
        )}

        {tab === 0 && (
        <Box sx={{ px: 4, py: 3 }}>
          {/* Description Section */}
          <Box
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)',
              p: 2.5,
              mb: 3,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DescriptionIcon sx={{ fontSize: 16, color: '#fff' }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#1E293B',
                  fontSize: '0.88rem',
                  letterSpacing: '-0.2px',
                }}
              >
                Description
              </Typography>
            </Stack>
            <Typography
              sx={{
                color: '#475569',
                fontSize: '0.88rem',
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
                pl: 0.5,
              }}
            >
              {task.description || (
                <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>No description provided</span>
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)',
              p: 2.5,
              mb: 3,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Category sx={{ fontSize: 16, color: '#fff' }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#1E293B',
                  fontSize: '0.88rem',
                  letterSpacing: '-0.2px',
                }}
              >
                Category
              </Typography>
            </Stack>
            <Typography
              sx={{
                color: '#475569',
                fontSize: '0.88rem',
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
                pl: 0.5,
              }}
            >
              {task.task_category_name || (
                <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>No category provided</span>
              )}
            </Typography>
          </Box>

          {/* Attachments Section */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #EC4899 0%, #F43F5E 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AttachFileIcon sx={{ fontSize: 16, color: '#fff' }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#1E293B',
                  fontSize: '0.88rem',
                  letterSpacing: '-0.2px',
                }}
              >
                Attachments
              </Typography>
              <Chip
                label={attachmentList.length}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  bgcolor: '#FCE7F3',
                  color: '#BE185D',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Stack>

            {attachmentList.length === 0 ? (
              <Box
                sx={{
                  borderRadius: 2,
                  background: '#F8FAFC',
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <AttachFileIcon sx={{ fontSize: 32, color: '#CBD5E1', mb: 0.5 }} />
                <Typography sx={{ color: '#94A3B8', fontSize: '0.82rem' }}>No attachments available</Typography>
              </Box>
            ) : (
              <GlobalAllTypeFileView attachmentFiles={task?.attachments} previewButtonName={'View'} />
            )}
          </Box>

          <Divider sx={{ my: 2, borderColor: '#F1F5F9' }} />

          {/* Details Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
            }}
          >
            <InfoRow icon={<BusinessIcon />} label="Source Department">
              <Typography sx={{ fontSize: '0.85rem', color: '#1E293B', fontWeight: 600 }}>
                {task.source_dept_name || '—'}
              </Typography>
            </InfoRow>

            <InfoRow icon={<BusinessIcon />} label="Target Department">
              <Typography sx={{ fontSize: '0.85rem', color: '#1E293B', fontWeight: 600 }}>
                {task.target_dept_name || '—'}
              </Typography>
            </InfoRow>

            <InfoRow icon={<PersonIcon />} label="Created By">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar
                  sx={{
                    width: 22,
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    background: `${getAvatarColor(task.created_by_name)}20`,
                    color: getAvatarColor(task.created_by_name),
                  }}
                >
                  {task.created_by_name?.charAt(0) || '?'}
                </Avatar>
                <Typography sx={{ fontSize: '0.85rem', color: '#1E293B', fontWeight: 600 }}>
                  {task.created_by_name || '—'}
                </Typography>
              </Stack>
            </InfoRow>

            <InfoRow icon={<AssignmentIndIcon />} label="Assigned To">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar
                  sx={{
                    width: 22,
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    background: `${getAvatarColor(task.assigned_to_name)}20`,
                    color: getAvatarColor(task.assigned_to_name),
                  }}
                >
                  {task.assigned_to_name?.charAt(0) || '?'}
                </Avatar>
                <Typography sx={{ fontSize: '0.85rem', color: '#1E293B', fontWeight: 600 }}>
                  {task.assigned_to_name || 'Unassigned'}
                </Typography>
              </Stack>
            </InfoRow>

            <InfoRow icon={<EventIcon />} label="Expected Delivery">
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  color: isOverdue ? '#EF4444' : '#1E293B',
                  fontWeight: 600,
                }}
              >
                {formatDate(task.expected_delivery_date)}
              </Typography>
            </InfoRow>

            <InfoRow icon={<AccessTimeIcon />} label="Created At">
              <Typography sx={{ fontSize: '0.85rem', color: '#1E293B', fontWeight: 600 }}>
                {formatDate(task.created_at)}
              </Typography>
            </InfoRow>
          </Box>
        </Box>
        )}
      </DialogContent>

      {/* Actions Footer */}
      <DialogActions
        sx={{
          px: 4,
          py: 2.5,
          background: '#F8FAFC',
          borderTop: '1px solid #F1F5F9',
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: '#64748B',
            px: 2.5,
            py: 1,
            borderRadius: 2,
            '&:hover': {
              background: '#F1F5F9',
            },
          }}
        >
          Close
        </Button>
        {/* // replace Button with this */}
        <Button
          onClick={() => handleDeleteClick(task.task_id)}
          disabled={deleteLoading}
          startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: '#fff',
            px: 2.5,
            py: 1,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
            transition: 'all 0.2s',
            '&:hover': {
              background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
              boxShadow: '0 6px 18px rgba(239, 68, 68, 0.4)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          {deleteLoading ? 'Deleting...' : 'Delete Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskDetailDialog;
